import { type ElementType, type ForwardedRef, forwardRef, type ReactElement, type ReactNode } from 'react'
import { CardActionArea, type CardActionAreaProps, CardContent } from '@mui/material'
import { useTheme } from '@mui/material/styles'

import * as commonStyles from '@DS/components/components/foundations/shapes/card/styles'
import Separator from '@DS/components/components/foundations/shapes/separator/Separator'

import BasicCard from './BasicCard'
import type { BaseBasicCardButtonProps, BasicCardButtonVariants, CustomBasicCardProps } from './BasicCardButton.types'
import * as styles from './styles'

export type BasicCardButtonProps<ComponentType extends ElementType = 'button'> = CustomBasicCardProps &
  BaseBasicCardButtonProps<ComponentType> &
  BasicCardButtonVariants & {
    bottomChild?: ReactNode
    children?: ReactNode
    hasAriaPressed?: boolean
    hasFocusVisible?: boolean
  }

const BasicCardButton = forwardRef(
  <ComponentType extends ElementType = 'button'>(
    {
      background,
      bottomChild,
      cardActionAreaProps,
      children,
      component,
      hasAriaPressed = false,
      hasFocusVisible = false,
      href,
      isDisabled = false,
      onClick,
      ...rest
    }: BasicCardButtonProps<ComponentType>,
    ref: ForwardedRef<HTMLDivElement>,
  ): ReactElement | null => {
    const theme = useTheme()
    const isAlert = background === 'alert'
    const isSelected = background === 'selected'

    const getBackground = () => {
      if (isAlert) {
        return {
          backgroundColor: theme.palette.error.main,
        }
      }

      if (isSelected) {
        return {
          backgroundColor: theme.palette.primary.main,
        }
      }

      return { background }
    }

    const getProps = () => {
      if (href && (!component || component === 'a')) {
        const { target, rel = target === '_blank' ? 'noopener noreferrer' : undefined }: CardActionAreaProps<'a'> =
          cardActionAreaProps || {}

        return { href, target, rel }
      }

      /* aria-pressed only needed in DayChoice */
      return hasAriaPressed ? { 'aria-pressed': isSelected } : null
    }

    return (
      <BasicCard ref={ref} {...rest} {...getBackground()}>
        <CardActionArea
          {...cardActionAreaProps}
          {...getProps()}
          {...(component && { component })}
          css={[
            cardActionAreaProps?.css,
            styles.basicCardButtonActionArea,
            isSelected && styles.selected(theme, hasFocusVisible),
            isAlert && styles.alert(theme),
            !!bottomChild && commonStyles.withHoverBottomChild,
            ...(isAlert || isSelected ? [styles.alertOrSelectedFocusVisibleStyle(theme)] : []),
          ]}
          onClick={onClick}
          disabled={isDisabled}
        >
          {children}
        </CardActionArea>
        {bottomChild && (
          <>
            <Separator background={background === 'default' ? 'paper' : 'default'} isNotOutline />
            <CardContent css={commonStyles.bottomChild}>{bottomChild}</CardContent>
          </>
        )}
      </BasicCard>
    )
  },
)

export default BasicCardButton
